import React from 'react';
import { Button, CircularProgress } from '@mui/material';
import PropTypes from 'prop-types';
import { PALETTE, GRADIENTS, SHADOWS, alpha } from '../theme';

/**
 * App-wide CTA button. Four visual variants:
 *   - primary:   filled coral gradient (main CTA)
 *   - secondary: coral outline → coral fill on hover
 *   - dark:      dark warm outline → dark fill on hover
 *   - ghost:     borderless muted text — quiet secondary action that should
 *                visually defer to a louder neighbor (e.g. Reset/Download
 *                sitting next to a hero product CTA)
 */
const VARIANT_STYLES = {
    primary: {
        background: GRADIENTS.primaryDeep,
        color: '#ffffff',
        boxShadow: SHADOWS.cta,
        '&:hover':    { transform: 'translateY(-2px)', boxShadow: SHADOWS.ctaHover },
        '&:disabled': { background: '#ccc', boxShadow: 'none', color: 'rgba(0, 0, 0, 0.26)' }
    },
    secondary: {
        border: '1px solid',
        borderColor: PALETTE.primary,
        color: PALETTE.primary,
        '&:hover': {
            backgroundColor: PALETTE.primary,
            color: '#ffffff',
            transform: 'translateY(-2px)',
            boxShadow: SHADOWS.cta
        },
        '&:disabled': { borderColor: '#ccc', color: '#ccc' }
    },
    dark: {
        border: '1px solid',
        borderColor: PALETTE.text,
        color: PALETTE.text,
        '&:hover': {
            backgroundColor: PALETTE.text,
            color: '#ffffff',
            transform: 'translateY(-2px)',
            boxShadow: `0 8px 25px ${alpha('text', 0.4)}`
        },
        '&:disabled': { borderColor: '#ccc', color: '#ccc' }
    },
    ghost: {
        border: 'none',
        backgroundColor: 'transparent',
        color: PALETTE.primary,
        boxShadow: 'none',
        '&:hover': {
            backgroundColor: alpha('primary', 0.08),
            color: PALETTE.primaryDark
        },
        '&:disabled': { color: '#ccc' }
    }
};

const ActionButton = ({
    children,
    onClick,
    disabled = false,
    startIcon,
    variant = 'primary',
    id,
    loading = false,
    sx = {}
}) => (
    <Button
        id={id}
        variant={variant === 'primary' ? 'contained' : variant === 'ghost' ? 'text' : 'outlined'}
        startIcon={loading ? <CircularProgress size={18} color="inherit" /> : startIcon}
        onClick={onClick}
        disabled={disabled || loading}
        sx={{
            width: { xs: '100%', sm: 'auto' },
            minWidth: { xs: 0, sm: variant === 'ghost' ? 0 : '160px' },
            height: variant === 'ghost' ? '40px' : '48px',
            borderRadius: 2,
            fontWeight: variant === 'ghost' ? 600 : 'bold',
            textTransform: 'none',
            transition: 'all 0.3s ease',
            ...VARIANT_STYLES[variant],
            ...sx
        }}
    >
        {children}
    </Button>
);

ActionButton.propTypes = {
    children: PropTypes.node.isRequired,
    onClick: PropTypes.func.isRequired,
    disabled: PropTypes.bool,
    startIcon: PropTypes.element,
    variant: PropTypes.oneOf(['primary', 'secondary', 'dark', 'ghost']),
    id: PropTypes.string,
    loading: PropTypes.bool,
    sx: PropTypes.object
};

export default ActionButton;
